import { cx, VariantProps } from 'class-variance-authority'
import React from 'react'
import { ReactNode } from 'react'

import { isEmailAddress, isHashUrl, isRelativeUrl, isTelephoneNumber, prependMailTo, prependTel } from '../../utils/url'
import { linkStyles } from './Link.styles'

export interface LinkProps extends VariantProps<typeof linkStyles> {
  href: string
  children: ReactNode
  id?: string
  className?: string
  ariaLabel?: string
  openInNewTab?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg'
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}

const Link = ({
  href,
  children,
  id,
  className,
  ariaLabel,
  openInNewTab,
  intent,
  status,
  size,
  onClick,
}: LinkProps): JSX.Element => {
  const isExternal = !isRelativeUrl(href) && !isEmailAddress(href) && !isTelephoneNumber(href) && !isHashUrl(href)

  if (isEmailAddress(href)) {
    href = prependMailTo(href)
  }

  if (isTelephoneNumber(href)) {
    href = prependTel(href)
  }

  if (isExternal) {
    return (
      <a
        id={id}
        href={href}
        aria-label={ariaLabel}
        className={cx(linkStyles({ intent, status, size }), className)}
        rel="noreferrer"
        target="_blank"
        onClick={onClick}
      >
        {children}
      </a>
    )
  }

  // We are unable to use Link from router-dom for now
  // due to the current structure.
  // Will need to spend time fixing the structure in <BaseTemplate /> before we can use it here
  // particularly how we get the content data, and resolve the entries
  return (
    // Internal links does not need rel="noreferrer"
    // eslint-disable-next-line react/jsx-no-target-blank
    <a
      id={id}
      href={href}
      aria-label={ariaLabel}
      className={cx(linkStyles({ intent, status, size }), className)}
      target={openInNewTab ? '_blank' : undefined}
      onClick={onClick}
    >
      {children}
    </a>
  )
}

export default Link
